import { useState } from "react";
import { Loader2 } from "lucide-react";
import { AISparkles } from "@/components/ui/ai-sparkles";
import { TruncatedText } from "@/components/ui/truncated-text";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { useGenerateSearchTerms } from "@/resources/search-term/search-term.api";

interface GenerateSearchTermsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: number;
  productName: string;
}

export function GenerateSearchTermsModal({
  open,
  onOpenChange,
  productId,
  productName,
}: GenerateSearchTermsModalProps) {
  const [type, setType] = useState<"single" | "grouped">("single");

  const generateMutation = useGenerateSearchTerms();

  const handleGenerate = () => {
    generateMutation.mutate(
      { product_id: productId, type },
      {
        onSuccess: () => {
          onOpenChange(false);
          toast({
            title: "Search Terms Generation Started",
            description: `Generating ${type === "single" ? "single (250 char)" : "grouped (5 × 500 char)"} search term set. This may take a moment.`,
          });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-card">
        <DialogHeader>
          <DialogTitle>Generate Search Terms</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Generate a new set of search terms using the highest KPS keywords from all landscapes for this hero product. Redundant, prohibited, and listing-referenced keywords will be automatically excluded.
        </p>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Product</Label>
            <p className="text-sm text-foreground">
              <TruncatedText lines={2}>{productName}</TruncatedText>
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Search Term Format</Label>
            <Select value={type} onValueChange={(v) => setType(v as "single" | "grouped")}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="single">
                  <div className="flex flex-col items-start">
                    <span className="font-medium">Single Set</span>
                    <span className="text-xs text-muted-foreground">One field, 250 character limit</span>
                  </div>
                </SelectItem>
                <SelectItem value="grouped">
                  <div className="flex flex-col items-start">
                    <span className="font-medium">Grouped (5 Sets)</span>
                    <span className="text-xs text-muted-foreground">5 keyword groups, 500 characters each</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Generation rules:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Uses highest KPS keywords from associated landscapes</li>
              <li>Strips keywords already in title, brand, or bullet points</li>
              <li>Removes prohibited keywords & competitor brands</li>
              <li>Eliminates conjunctions, prepositions, and duplicates</li>
              <li>Prioritizes unique, high-value individual keywords</li>
            </ul>
          </div>

          <Button
            className="w-full"
            onClick={handleGenerate}
            disabled={generateMutation.isPending}
          >
            {generateMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <AISparkles className="mr-2" />
                Generate Search Terms
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
